import { Injectable } from '@angular/core';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { Observable, Subject } from 'rxjs';
import { SocketConfig, SocketMessage } from './websocket.models';

@Injectable({
  providedIn: 'root',
})
export class WebsocketService {
  private connection: HubConnection | null = null;
  private subscribedChannels = new Set<string>();

  private readonly messageSubject = new Subject<SocketMessage>();
  private readonly openSubject = new Subject<void>();
  private readonly closeSubject = new Subject<void>();
  private readonly reconnectingSubject = new Subject<void>();
  private readonly errorSubject = new Subject<string>();

  readonly messages$: Observable<SocketMessage> =
    this.messageSubject.asObservable();
  readonly opened$: Observable<void> = this.openSubject.asObservable();
  readonly closed$: Observable<void> = this.closeSubject.asObservable();
  readonly reconnecting$: Observable<void> =
    this.reconnectingSubject.asObservable();
  readonly errors$: Observable<string> = this.errorSubject.asObservable();

  async connect(config: SocketConfig): Promise<void> {
    if (
      this.connection &&
      (this.connection.state === HubConnectionState.Connected ||
        this.connection.state === HubConnectionState.Connecting ||
        this.connection.state === HubConnectionState.Reconnecting)
    ) {
      return;
    }

    console.log('[Websocket] connecting...', config.url);

    this.connection = new HubConnectionBuilder()
      .withUrl(config.url, {
        accessTokenFactory: () => config.token,
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();

    this.registerHandlers(this.connection);

    await this.connection.start();

    console.log('[Websocket] connected');
    this.openSubject.next();

    if (this.subscribedChannels.size) {
      for (const channel of this.subscribedChannels) {
        await this.subscribe(channel);
      }
    }
  }

  async disconnect(): Promise<void> {
    const current = this.connection;
    this.connection = null;
    this.subscribedChannels.clear();

    if (!current) {
      return;
    }

    await current.stop();
  }

  async subscribe(channel: string): Promise<void> {
    this.subscribedChannels.add(channel);

    if (
      !this.connection ||
      this.connection.state !== HubConnectionState.Connected
    ) {
      return;
    }

    console.log('[Websocket] subscribing to', channel);
    await this.connection.invoke('Subscribe', channel);
    console.log('[Websocket] subscribed to', channel);
  }

  async unsubscribe(channel: string): Promise<void> {
    this.subscribedChannels.delete(channel);

    if (
      !this.connection ||
      this.connection.state !== HubConnectionState.Connected
    ) {
      return;
    }

    console.log('[Websocket] unsubscribing from', channel);
    await this.connection.invoke('Unsubscribe', channel);
    console.log('[Websocket] unsubscribed from', channel);
  }

  isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }

  private registerHandlers(connection: HubConnection): void {
    connection.off('ReceiveMessage');

    connection.on('ReceiveMessage', (message: SocketMessage) => {
      console.log('[Websocket] message received', message);
      this.messageSubject.next(message);
    });

    connection.onreconnecting(() => {
      this.reconnectingSubject.next();
    });

    connection.onreconnected(async () => {
      this.openSubject.next();

      for (const channel of this.subscribedChannels) {
        await this.subscribe(channel);
      }
    });

    connection.onclose((error) => {
      if (error?.message) {
        this.errorSubject.next(error.message);
      }

      this.closeSubject.next();
    });
  }
}